import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { RefreshCw, ExternalLink, ShieldAlert, Sparkles } from "lucide-react";
import PeriodBadge from "./PeriodBadge";

const SEASON_COLORS = {
  Spring: "#7ba43a",
  Summer: "#e5a02b",
  Fall: "#a3612c",
  Winter: "#3a6b9a",
  Transition: "#c72c2c",
  Unclear: "#6b6b6b",
};

function timeAgo(iso) {
  if (!iso) return "—";
  const s = Math.round((Date.now() - new Date(iso)) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

export default function CompetitorPanel() {
  const [rows, setRows] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [loaded, setLoaded] = useState(false);

  async function load() {
    try {
      const { data } = await api.get("/competitors/latest");
      setRows(data);
    } catch (e) {}
    setLoaded(true);
  }
  useEffect(() => { load(); }, []);

  async function scan() {
    setScanning(true);
    try {
      const { data } = await api.post("/competitors/scan");
      setRows(data.results);
    } catch (e) {}
    setScanning(false);
  }

  return (
    <div className="border border-[#0a0a0a] bg-[#ede8dc] p-6 lg:p-8" data-testid="competitor-panel">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="bm-eyebrow mb-2">Table 7 &middot; Regional competitor activity</div>
          <h3 className="bm-headline text-3xl lg:text-4xl">What the neighbours are pushing.</h3>
          <PeriodBadge label="Latest scan" note="live web snapshot, no date range" className="mt-1" />
          <p className="mt-2 text-sm text-neutral-700 max-w-2xl">
            Claude reads competitor homepages and flags each store's dominant merchandising story plus a season signal — a leading indicator for when to swap in your own transition drops.
          </p>
        </div>
        <button
          onClick={scan}
          disabled={scanning}
          data-testid="scan-competitors"
          className="bm-eyebrow px-4 py-3 border border-[#0a0a0a] bg-[#0a0a0a] text-[#f5f2eb] hover:bg-[#c72c2c] disabled:opacity-50 flex items-center gap-2"
        >
          <RefreshCw size={13} className={scanning ? "animate-spin" : ""} />
          {scanning ? "Scanning…" : "Rescan competitors"}
        </button>
      </div>

      {loaded && rows.length === 0 && (
        <div className="text-sm text-neutral-600 font-mono-bm">No scans yet. Press Rescan to pull fresh reads.</div>
      )}

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {rows.map((r) => {
          const a = r.analysis || {};
          const seasonColor = SEASON_COLORS[a.season_signal] || "#6b6b6b";
          const isBlocked = r.status === "blocked";
          const isError = r.status === "error";
          const ok = r.status === "ok";
          return (
            <div
              key={r.competitor_key}
              data-testid={`competitor-card-${r.competitor_key}`}
              className="border border-[#0a0a0a] bg-[#f5f2eb] p-5 flex flex-col"
            >
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="min-w-0">
                  <div className="bm-headline text-2xl">{r.name}</div>
                  <div className="bm-eyebrow text-neutral-500 mt-1">{r.region}</div>
                </div>
                <a
                  href={r.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-neutral-500 hover:text-[#c72c2c]"
                  title="Open homepage"
                >
                  <ExternalLink size={14} />
                </a>
              </div>

              {ok && (
                <>
                  <div className="mb-3">
                    <span
                      className="inline-block px-2 py-1 bm-eyebrow text-[#f5f2eb]"
                      style={{ background: seasonColor }}
                    >
                      {a.season_signal} · {a.season_confidence} confidence
                    </span>
                  </div>
                  <p className="text-sm leading-relaxed mb-3">{a.main_focus}</p>
                  {a.hero_products?.length > 0 && (
                    <div className="mb-3">
                      <div className="bm-eyebrow text-neutral-500 mb-1">Hero items</div>
                      <div className="flex flex-wrap gap-1">
                        {a.hero_products.map((h, i) => (
                          <span key={i} className="text-xs font-mono-bm border border-[#0a0a0a]/40 px-2 py-0.5">{h}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {a.notable_promotions && (
                    <div className="mb-2 text-xs font-mono-bm text-neutral-700">
                      <span className="bm-eyebrow text-neutral-500 mr-2">Promo</span>
                      {a.notable_promotions}
                    </div>
                  )}
                  {a.vintage_relevance && (
                    <div className="mt-auto pt-3 border-t border-[#0a0a0a]/20 text-xs italic text-[#c72c2c] flex items-start gap-1">
                      <Sparkles size={12} className="mt-0.5 flex-shrink-0" />
                      <span>{a.vintage_relevance}</span>
                    </div>
                  )}
                </>
              )}

              {isBlocked && (
                <div className="flex items-start gap-2 text-sm text-neutral-600 py-3">
                  <ShieldAlert size={16} className="mt-0.5 flex-shrink-0 text-[#c72c2c]" />
                  <div>
                    <div className="font-medium">Site blocks automated access</div>
                    <div className="bm-eyebrow text-neutral-500 mt-1">Enterprise bot protection — a paid scraper would be needed to bypass.</div>
                  </div>
                </div>
              )}

              {isError && (
                <div className="text-sm text-neutral-600 py-3">
                  <div className="bm-eyebrow text-[#c72c2c] mb-1">Scan failed</div>
                  <div className="text-xs font-mono-bm">{r.error}</div>
                </div>
              )}

              <div className="mt-auto pt-3 bm-eyebrow text-neutral-500 flex items-center justify-between">
                <span>Scanned {timeAgo(r.scanned_at)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
